/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import Badge from '@mui/material/Badge';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import { Alert, Snackbar } from '@mui/material';

function PosMessage({ reload, count, setBlockScan }) {
  const mod = 'message';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [record, setRecord] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const btnRef = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const view = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/open`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status === 'ok') {
          setRecord(result.data);
          setModalOpen(true);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const close = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const renderList = () => {
    return (
      <MDBox>
        <List component='nav'>
          {record.map((row) => (
            <ListItemButton alignItems='flex-start' key={row.id}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'red' }}>
                  <i className='far fa-envelope' />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <MDBox>
                    <span>{row.name}</span>
                  </MDBox>
                }
                secondary={
                  <Typography
                    component='span'
                    variant='body2'
                    sx={{ color: 'text.primary', display: 'inline' }}
                  >
                    {Func.dateTimeDisplay(row.updated_at)}
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          กล่องข้อความ
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, overflow: 'hidden' }}>
          {renderList()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Tooltip title='กล่องข้อความ' sx={{ mr: 2 }}>
        <Badge badgeContent={count} color='error'>
          <IconButton size='small' ref={btnRef} onClick={view}>
            <i className='fad fa-envelope-open-text' />
          </IconButton>
        </Badge>
      </Tooltip>
      {renderOpen()}
      {notify}
    </>
  );
}

PosMessage.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
};

export default PosMessage;
